// src/pages/SubPayComponents.js
//
// Shared pieces for recording subcontractor payments. Used in two places on
// JobEstimateSpreadsheet:
//   1. the Sub Pay popup on a job row (payment is tied to that job)
//   2. the Subcontractor Payables panel (payment is against the overall balance)
//
// The parent owns all Firestore writes. These components only collect input
// and hand back a list of payment rows.

import React, { useEffect, useMemo, useState } from "react";
import {
  SUB_PAYMENT_METHODS,
  PAY_FREQUENCIES,
  MAX_SCHEDULE_PAYMENTS,
  buildPaymentSchedule,
  paymentsNeededFor,
  todayLocalKey,
} from "../utils/subPaySchedule";
import "./SubPay.css";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const NEW_NAME_KEY = "__new__";

// "1045" -> "1046". Anything that doesn't end in digits is left alone.
const bumpReference = (ref, step) => {
  const match = /^(.*?)(\d+)$/.exec(String(ref || "").trim());
  if (!match || step === 0) return ref || "";
  const next = String(Number(match[2]) + step).padStart(match[2].length, "0");
  return `${match[1]}${next}`;
};

/* ========================================================================== */
/* Payment entry                                                               */
/* ========================================================================== */

export function SubPaymentEntry({
  contractorOptions = [],
  defaultContractorKey = "",
  allowSchedule = true,
  allowNewName = true,
  busy = false,
  submitLabel = "Record Payment",
  intro = "",
  onSubmit,
}) {
  const [contractorKey, setContractorKey] = useState(defaultContractorKey);
  const [newName, setNewName] = useState("");
  const [amount, setAmount] = useState("");
  const [firstDate, setFirstDate] = useState(todayLocalKey());
  const [method, setMethod] = useState("Check");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [count, setCount] = useState(4);
  const [stopAtBalance, setStopAtBalance] = useState(false);
  const [rowEdits, setRowEdits] = useState({});
  const [removedRows, setRemovedRows] = useState({});

  useEffect(() => {
    if (defaultContractorKey) setContractorKey(defaultContractorKey);
  }, [defaultContractorKey]);

  const selected = contractorOptions.find((opt) => opt.key === contractorKey) || null;
  const balance = Number(selected?.balance || 0);
  const isPlan = allowSchedule && frequency !== "once";
  const canStopAtBalance = isPlan && balance > 0;

  const planRows = useMemo(
    () =>
      buildPaymentSchedule({
        firstDate,
        frequency: isPlan ? frequency : "once",
        count,
        amount,
        capTotal: canStopAtBalance && stopAtBalance ? balance : 0,
      }),
    [firstDate, frequency, isPlan, count, amount, canStopAtBalance, stopAtBalance, balance]
  );

  // Any change to the plan itself throws away per-row edits.
  useEffect(() => {
    setRowEdits({});
    setRemovedRows({});
  }, [firstDate, frequency, count, amount, stopAtBalance, contractorKey]);

  const rows = planRows
    .map((row, index) => {
      const edit = rowEdits[index] || {};
      return {
        index,
        date: edit.date ?? row.date,
        amount: edit.amount ?? row.amount,
        reference:
          edit.reference ??
          (isPlan && method === "Check" ? bumpReference(reference, index) : reference),
        note: edit.note ?? note,
      };
    })
    .filter((row) => !removedRows[row.index]);

  const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const today = todayLocalKey();
  const futureCount = rows.filter((row) => row.date > today).length;

  const editRow = (index, key, value) =>
    setRowEdits((prev) => ({ ...prev, [index]: { ...(prev[index] || {}), [key]: value } }));

  const reset = () => {
    setAmount("");
    setReference("");
    setNote("");
    setFrequency("once");
    setStopAtBalance(false);
    setNewName("");
    setRowEdits({});
    setRemovedRows({});
  };

  const submit = async () => {
    const typedName = newName.trim();
    if (!contractorKey || (contractorKey === NEW_NAME_KEY && !typedName)) {
      alert("Pick the subcontractor you paid.");
      return;
    }
    if (!(Number(amount) > 0)) {
      alert("Enter the amount paid.");
      return;
    }
    if (!rows.length) {
      alert("There are no payments to record. Check the first payment date.");
      return;
    }
    const bad = rows.find(
      (row) => !(Number(row.amount) > 0) || !/^\d{4}-\d{2}-\d{2}$/.test(row.date || "")
    );
    if (bad) {
      alert(`Payment #${bad.index + 1} needs a date and an amount greater than $0.`);
      return;
    }
    if (futureCount > 0) {
      const ok = window.confirm(
        `${futureCount} of these payments are dated after today. They will count as paid right away.\n\nRecord them anyway?`
      );
      if (!ok) return;
    }

    const who = contractorKey === NEW_NAME_KEY ? typedName : selected?.label || "";
    if (rows.length > 1) {
      const ok = window.confirm(
        `Record ${rows.length} payments to ${who} totaling ${money(total)}?`
      );
      if (!ok) return;
    }

    const saved = await onSubmit?.({
      contractorKey: contractorKey === NEW_NAME_KEY ? "" : contractorKey,
      newName: contractorKey === NEW_NAME_KEY ? typedName : "",
      frequency: isPlan ? frequency : "once",
      entries: rows.map((row) => ({
        date: row.date,
        amount: Number(row.amount),
        method,
        reference: row.reference || "",
        note: row.note || "",
      })),
    });

    if (saved) reset();
  };

  return (
    <div className="subpay-entry">
      {intro ? <p className="subpay-muted">{intro}</p> : null}

      <div className="subpay-grid">
        <label className="subpay-field subpay-span-2">
          <span>Subcontractor</span>
          <select value={contractorKey} onChange={(e) => setContractorKey(e.target.value)}>
            <option value="">Select contractor</option>
            {contractorOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
                {Number.isFinite(opt.balance) ? ` — balance ${money(opt.balance)}` : ""}
              </option>
            ))}
            {allowNewName && <option value={NEW_NAME_KEY}>Someone not listed…</option>}
          </select>
        </label>

        {contractorKey === NEW_NAME_KEY && (
          <label className="subpay-field subpay-span-2">
            <span>Name as it should appear</span>
            <input
              value={newName}
              placeholder="Company or person name"
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
        )}

        <label className="subpay-field">
          <span>{isPlan ? "Amount per payment" : "Amount paid"}</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>

        <label className="subpay-field">
          <span>{isPlan ? "First payment date" : "Date paid"}</span>
          <input type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)} />
        </label>

        <label className="subpay-field">
          <span>Payment type</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            {SUB_PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label className="subpay-field">
          <span>{method === "Check" ? "Check #" : "Reference"}</span>
          <input
            value={reference}
            placeholder={method === "Check" ? "1045" : "Confirmation / memo"}
            onChange={(e) => setReference(e.target.value)}
          />
        </label>

        <label className="subpay-field subpay-span-2">
          <span>Notes</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {allowSchedule && (
          <label className="subpay-field">
            <span>Repeat</span>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              {PAY_FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {isPlan && (
          <label className="subpay-field">
            <span>Number of payments</span>
            <input
              type="number"
              min="1"
              max={MAX_SCHEDULE_PAYMENTS}
              value={count}
              disabled={canStopAtBalance && stopAtBalance}
              onChange={(e) => setCount(e.target.value)}
            />
          </label>
        )}
      </div>

      {canStopAtBalance && (
        <label className="subpay-check">
          <input
            type="checkbox"
            checked={stopAtBalance}
            onChange={(e) => setStopAtBalance(e.target.checked)}
          />
          <span>
            Pay off the {money(balance)} balance
            {Number(amount) > 0
              ? ` — ${paymentsNeededFor(balance, amount)} payments, last one covers what's left`
              : ""}
          </span>
        </label>
      )}

      {isPlan && rows.length > 0 && (
        <div className="subpay-plan">
          <div className="subpay-plan-head">
            <strong>
              {rows.length} payment{rows.length === 1 ? "" : "s"} · {money(total)}
            </strong>
            {selected && Number.isFinite(selected.balance) && (
              <span>Balance after: {money(balance - total)}</span>
            )}
          </div>
          <p className="subpay-muted">
            Edit any date, amount, or check # below before recording.
            {method === "Check" ? " Check numbers count up from the first one." : ""}
          </p>
          <div className="subpay-table-wrap">
            <table className="subpay-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>{method === "Check" ? "Check #" : "Reference"}</th>
                  <th aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.index} className={row.date > today ? "subpay-future" : ""}>
                    <td>{row.index + 1}</td>
                    <td>
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) => editRow(row.index, "date", e.target.value)}
                      />
                      {row.date > today && <small className="subpay-badge">future</small>}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.amount}
                        onChange={(e) => editRow(row.index, "amount", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={row.reference}
                        onChange={(e) => editRow(row.index, "reference", e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="subpay-x"
                        aria-label={`Remove payment ${row.index + 1}`}
                        onClick={() => setRemovedRows((prev) => ({ ...prev, [row.index]: true }))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {futureCount > 0 && (
        <div className="subpay-warn">
          {futureCount} payment{futureCount === 1 ? " is" : "s are"} dated after today.
        </div>
      )}

      <div className="subpay-actions">
        <button type="button" className="subpay-primary" disabled={busy} onClick={submit}>
          {busy
            ? "Saving…"
            : isPlan && rows.length > 1
            ? `Record ${rows.length} Payments`
            : submitLabel}
        </button>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Payment history                                                             */
/* ========================================================================== */

export function SubPaymentHistory({
  payments = [],
  contractorOptions = [],
  matchesContractor,
  showJob = true,
  showFilter = true,
  emptyText = "No subcontractor payments recorded yet.",
  busyId = "",
  onDelete,
}) {
  const [filterKey, setFilterKey] = useState("");

  const visible = useMemo(() => {
    const list = filterKey
      ? payments.filter((p) => matchesContractor?.(p, filterKey))
      : payments;
    return [...list].sort(
      (a, b) =>
        String(b.date || "").localeCompare(String(a.date || "")) ||
        String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
    );
  }, [payments, filterKey, matchesContractor]);

  const total = visible.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <div className="subpay-history">
      {showFilter && (
        <div className="subpay-history-bar">
          <select value={filterKey} onChange={(e) => setFilterKey(e.target.value)}>
            <option value="">All subcontractors</option>
            {contractorOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
          <span>
            {visible.length} payment{visible.length === 1 ? "" : "s"} · {money(total)}
          </span>
        </div>
      )}

      <div className="subpay-table-wrap">
        <table className="subpay-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Subcontractor</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Check # / Ref</th>
              {showJob && <th>Job</th>}
              <th>Notes</th>
              {onDelete && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={`${p.sourceCollection || "subcontractorPay"}-${p.id}`}>
                <td>{p.date || ""}</td>
                <td>{p.subcontractorName || ""}</td>
                <td className="subpay-num">{money(p.amount)}</td>
                <td>{p.method || ""}</td>
                <td>{p.reference || ""}</td>
                {showJob && <td>{p.jobLabel || <span className="subpay-muted">Balance</span>}</td>}
                <td>
                  {p.note || ""}
                  {p.payFrequency && p.payFrequency !== "once" && p.scheduleCount ? (
                    <small className="subpay-badge">
                      plan {Number(p.scheduleIndex || 0) + 1}/{p.scheduleCount}
                    </small>
                  ) : null}
                </td>
                {onDelete && (
                  <td>
                    <button
                      type="button"
                      className="subpay-link-danger"
                      disabled={busyId === p.id}
                      onClick={() => onDelete(p)}
                    >
                      {busyId === p.id ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={8} className="subpay-muted">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
